import {
  Experiment,
  FeatureResult,
  GrowthBook,
  GrowthBookProvider,
  Result,
} from '@growthbook/growthbook-react';

import { datadogRum } from '@datadog/browser-rum';
import { useQual } from 'hooks/useQual';
import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { onUpdate, offUpdate, CanonicalUserProfile } from '@updater/identity';
import { useEnvironment } from 'services/environment';

export const GrowthbookReadyContext = createContext<boolean>(false);

export type TrackingCallback = (
  experiment: Experiment<any>,
  result: Result<any>
) => void;
type FeatureUsageCallback = (featureKey: string, result: FeatureResult) => void;

const trackingSubscriptions = new Set<TrackingCallback>();
const featureUsageSubscriptions = new Set<FeatureUsageCallback>();

export const useGrowthbookTracking = (cb: TrackingCallback) => {
  useEffect(() => {
    trackingSubscriptions.add(cb);
    return () => {
      trackingSubscriptions.delete(cb);
    };
  }, [cb]);
};

export const useGrowthbookFeatureUsage = (cb: FeatureUsageCallback) => {
  useEffect(() => {
    featureUsageSubscriptions.add(cb);
    return () => {
      featureUsageSubscriptions.delete(cb);
    };
  }, [cb]);
};

const datadogFeatureNotifier: FeatureUsageCallback = (featureKey, result) => {
  datadogRum.addFeatureFlagEvaluation(featureKey, result.value);
};

const useCreateGrowthbookClient = () => {
  const env = useEnvironment();
  const growthbookAPIHost = env.growthbookApiHost;
  const growthbookSDKKey = env.growthbookSdkKey;
  const enableDevMode = !env.inProduction;

  return useMemo(() => {
    return new GrowthBook({
      apiHost: growthbookAPIHost,
      clientKey: growthbookSDKKey,
      enableDevMode,
      trackingCallback: (experiment, result) => {
        trackingSubscriptions.forEach((cb) => {
          try {
            cb(experiment, result);
          } catch (e) {
            console.error(e);
          }
        });
      },
      onFeatureUsage: (featureKey, result) => {
        featureUsageSubscriptions.forEach((cb) => {
          try {
            cb(featureKey, result);
          } catch (e) {
            console.error(e);
          }
        });
      },
    });
  }, [growthbookAPIHost, growthbookSDKKey, enableDevMode]);
};

export type GrowthbookWrapperProps = {
  children: React.ReactNode;
};

export const GrowthbookWrapper: React.FC<GrowthbookWrapperProps> = ({
  children,
}) => {
  const gbClient = useCreateGrowthbookClient();
  const [isGrowthbookReady, setIsGrowthbookReady] = useState(false);

  useEffect(() => {
    setIsGrowthbookReady(false);
    // Load features from the GrowthBook API
    gbClient.loadFeatures({ timeout: 4000 }).then(() => {
      setIsGrowthbookReady(true);
    });
  }, [gbClient]);

  const { uuid, promoId } = useQual();

  useGrowthbookFeatureUsage(datadogFeatureNotifier);

  // Initial qual-based attributes — set before identity fires
  useEffect(() => {
    gbClient.setAttributes({ id: uuid, promoId });
  }, [gbClient, uuid, promoId]);

  const gbClientRef = useRef(gbClient);
  gbClientRef.current = gbClient;

  // Identity-driven updates — spread full canonical profile on top of qual attributes
  useEffect(() => {
    const listener = (profile: CanonicalUserProfile) => {
      const { updUuid, ...profileRest } = profile;
      gbClientRef.current.setAttributes({
        id: updUuid,
        ...profileRest,
      });
    };
    onUpdate(listener);
    return () => offUpdate(listener);
  }, []);

  return (
    <GrowthBookProvider growthbook={gbClient}>
      <GrowthbookReadyContext.Provider value={isGrowthbookReady}>
        {children}
      </GrowthbookReadyContext.Provider>
    </GrowthBookProvider>
  );
};
